import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, takeUntil, distinctUntilChanged, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ChatStateService } from './chat-state.service';
import { PlanReviewerService } from '../shared/plan-reviewer.service';
import { ProjectCreationService } from '../shared/project-creation.service';
import { ChatContext } from '../models/chat.models';

interface RouteChatContext {
  projectId: string;
  fileId?: string;
  pageNumber?: number;
  tab?: string;
}

/**
 * Service that automatically syncs application context to chat context.
 *
 * Watches:
 * - Router navigation (project ID, file ID, page number, tab)
 * - Active page selection
 * - Plan metadata (project name, file names)
 *
 * Updates ChatStateService context automatically so the agent knows:
 * - What project the user is viewing
 * - What file they're looking at
 * - What page they're on
 * - What tab is active
 *
 * Issue #263: Agentic Chat - Context Management
 */
@Injectable({
  providedIn: 'root',
})
export class ChatContextSyncService implements OnDestroy {
  private router = inject(Router);
  private chatState = inject(ChatStateService);
  private planReviewer = inject(PlanReviewerService);
  private projectCreationService = inject(ProjectCreationService);

  private destroy$ = new Subject<void>();
  private routeUpdateSequence = 0;
  private lastRequestedRouteContext?: RouteChatContext;

  constructor() {
    this.initializeContextSync();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Synchronizes the current route before a context-sensitive action starts.
   *
   * Route identity is applied synchronously so callers never have to wait for
   * project metadata or plan loading before using the active file and page.
   */
  syncCurrentRoute(): ChatContext {
    this.updateChatContext(this.extractContextFromRoute());
    return this.chatState.currentState.context;
  }

  /**
   * Initialize automatic context synchronization.
   * Watches router events and updates chat context.
   */
  private initializeContextSync() {
    // Watch router navigation events
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.extractContextFromRoute()),
        distinctUntilChanged(
          (prev, curr) =>
            prev.projectId === curr.projectId &&
            prev.fileId === curr.fileId &&
            prev.pageNumber === curr.pageNumber &&
            prev.tab === curr.tab
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(context => {
        ClientLoggingService.log('INFO', '[ChatContextSync] Route context changed:', context);
        this.updateChatContext(context);
      });

    // Set initial context
    const initialContext = this.extractContextFromRoute();
    if (initialContext.projectId) {
      this.updateChatContext(initialContext);
    }
  }

  /**
   * Extract context from current route.
   */
  private extractContextFromRoute() {
    let planId = '';
    let fileId: string | undefined;
    let pageNumber: number | undefined;
    let tab: string | undefined;

    ClientLoggingService.log('INFO', 'ChatContextSyncService: Starting route traversal');

    // Traverse route tree to find params
    // We need to traverse depth-first or check all children to find the active route with params
    const queue: ActivatedRoute[] = [this.router.routerState.root];

    while (queue.length > 0) {
      const current = queue.shift()!;

      if (current.snapshot.params['planId']) {
        planId = current.snapshot.params['planId'];
      }
      if (current.snapshot.params['fileId']) {
        fileId = current.snapshot.params['fileId'];
      }
      if (current.snapshot.params['pageNumber']) {
        pageNumber = +current.snapshot.params['pageNumber'];
      }
      if (current.snapshot.queryParams['tab']) {
        tab = current.snapshot.queryParams['tab'];
      }

      if (current.children) {
        queue.push(...current.children);
      }
    }

    return {
      projectId: planId,
      fileId,
      pageNumber,
      tab,
    };
  }

  /**
   * Update chat context with route information and metadata.
   */
  private updateChatContext(routeContext: RouteChatContext): void {
    if (!routeContext.projectId) {
      this.routeUpdateSequence++;
      this.lastRequestedRouteContext = undefined;
      this.clearChatContext();
      return;
    }

    if (this.isSameRequestedRoute(routeContext)) {
      return;
    }

    this.lastRequestedRouteContext = { ...routeContext };
    const updateSequence = ++this.routeUpdateSequence;
    const currentContext = this.chatState.currentState.context;
    const isSameProject = currentContext.projectId === routeContext.projectId;
    const isSameFile =
      isSameProject &&
      (currentContext.fileId === routeContext.fileId ||
        (currentContext.fileId === 'legacy' && routeContext.fileId === '1'));

    // Apply the route identity before any asynchronous metadata lookup. Voice
    // startup and other immediate actions must use the page visible in the URL.
    this.chatState.updateContext({
      projectId: routeContext.projectId,
      projectName: isSameProject ? currentContext.projectName : undefined,
      projectAddress: isSameProject ? currentContext.projectAddress : undefined,
      fileId: isSameFile ? currentContext.fileId : routeContext.fileId,
      fileName: isSameFile ? currentContext.fileName : routeContext.fileId ? `File ${routeContext.fileId}` : undefined,
      pageNumber: routeContext.pageNumber,
      tab: routeContext.tab,
    });

    void this.enrichChatContext(routeContext, updateSequence);
  }

  private isSameRequestedRoute(routeContext: RouteChatContext): boolean {
    const requested = this.lastRequestedRouteContext;
    return (
      requested !== undefined &&
      requested.projectId === routeContext.projectId &&
      requested.fileId === routeContext.fileId &&
      requested.pageNumber === routeContext.pageNumber &&
      requested.tab === routeContext.tab
    );
  }

  private async enrichChatContext(routeContext: RouteChatContext, updateSequence: number): Promise<void> {
    try {
      const [{ projectName, projectAddress }, { fileId: actualFileId, fileName }] = await Promise.all([
        this.getProjectMetadata(routeContext.projectId),
        this.resolveFileContextForBackend(routeContext.projectId, routeContext.fileId),
      ]);

      // An older navigation may finish loading after a newer one. Ignore it
      // rather than restoring the previous project's file or page context.
      if (updateSequence !== this.routeUpdateSequence) {
        ClientLoggingService.log('DEBUG', '[ChatContextSync] Ignoring stale route metadata:', routeContext);
        return;
      }

      // Update chat context with resolved values
      this.chatState.updateContext({
        projectId: routeContext.projectId,
        projectName: projectName,
        projectAddress: projectAddress,
        fileId: actualFileId,
        fileName: fileName,
        pageNumber: routeContext.pageNumber,
        tab: routeContext.tab,
      });

      this.logContextUpdate(routeContext, actualFileId, projectName, fileName);
    } catch (error) {
      if (updateSequence !== this.routeUpdateSequence) {
        return;
      }
      this.lastRequestedRouteContext = undefined;
      ClientLoggingService.log('ERROR', '[ChatContextSync] Failed to enrich chat context:', error);
    }
  }

  /**
   * Clears chat context when not on a project page.
   */
  private clearChatContext(): void {
    this.chatState.updateContext({
      projectId: undefined,
      projectName: undefined,
      fileId: undefined,
      fileName: undefined,
      pageNumber: undefined,
      tab: undefined,
    });
  }

  /**
   * Gets the project name and address.
   */
  private async getProjectMetadata(projectId: string): Promise<{ projectName: string; projectAddress?: string }> {
    try {
      const response = await this.projectCreationService.getProjectMetadata(projectId);
      if (response.success && response.metadata) {
        let addressStr: string | undefined;
        if (response.metadata.address) {
          addressStr = response.metadata.address.formattedAddress || response.metadata.address.street;
        }
        return {
          projectName: response.metadata.project_name || projectId,
          projectAddress: addressStr,
        };
      }
    } catch (e) {
      ClientLoggingService.log('WARN', '[ChatContextSync] Failed to pull project metadata, defaulting title to ID:', e);
    }
    return { projectName: projectId };
  }

  /**
   * Resolves the fileId and fileName that should be sent to backend API calls.
   *
   * **Critical distinction:**
   * - URL routes may show `/files/1/` for ALL projects (UI consistency)
   * - Backend API calls must OMIT fileId for legacy projects (empty string)
   *
   * **Why this matters:**
   * - Legacy projects: GCS paths are `projects/{id}/pages/{num}/` (no `/files/`)
   * - Modern projects: GCS paths are `projects/{id}/files/{fileId}/pages/{num}/`
   * - Backend matches pages by GCS path, not URL route
   *
   * @param projectId The project ID
   * @param routeFileId The fileId from the URL route (may be "1" for legacy projects)
   * @returns Object with fileId and fileName to send to backend
   *          - fileId is undefined for legacy projects (backend gets empty string)
   *          - fileId is set for modern multi-file projects
   */
  private async resolveFileContextForBackend(
    projectId: string,
    routeFileId?: string
  ): Promise<{ fileId: string | undefined; fileName: string | undefined }> {
    if (!routeFileId) {
      return { fileId: undefined, fileName: undefined };
    }

    // Try to get pages from backend (uses plan reviewer's internal cache)
    let pages = null;
    try {
      const plan = await this.planReviewer.getArchitecturalPlan(projectId);
      pages = plan?.getPagesList();
    } catch (error) {
      ClientLoggingService.log('WARN', '[ChatContextSync] Failed to fetch plan from backend:', error);
      // Will use pages = null and fall through to legacy detection below
    }

    if (!pages || pages.length === 0) {
      // No pages available.
      // WARNING: Defaulting to legacy for fileId="1" is dangerous for modern projects that are slow to load.
      // However, we return routeFileId as-is (modern assumption) to avoid showing "Legacy" incorrectly.
      // If it's truly a legacy project, the backend call might fail if we send a fileId,
      // but the UI will at least look correct (File 1) instead of confusingly "Legacy".
      return { fileId: routeFileId, fileName: `File ${routeFileId}` };
    }

    // Detect project structure by inspecting GCS storage paths
    const isLegacyProject = PlanReviewerService.isLegacyProject(pages);

    if (isLegacyProject && routeFileId === '1') {
      // Legacy project: URL route has /files/1/ but GCS has no /files/ segment
      // Backend API receives fileId="legacy" sentinel value to trigger legacy lookup
      ClientLoggingService.log(
        'INFO',
        '[ChatContextSync] Legacy project detected - using fileId="legacy" for backend API calls'
      );
      return { fileId: 'legacy', fileName: undefined };
    }

    // Modern multi-file project: Send fileId and lookup fileName
    ClientLoggingService.log('INFO', '[ChatContextSync] Modern project - including fileId in backend API calls');

    // Find page that matches the route file ID by inspecting its folder path
    // Note: ArchitecturalPlanPage does not have getFileId(), we must extract it from the path
    const pageInFile = pages.find((p: any) => {
      const path = p.getPageFolderPath ? p.getPageFolderPath() : '';
      const match = path && path.match(/\/files\/(\d+)\/pages\//);
      return match && match[1] === routeFileId;
    });

    // Try to get file name if available (though it might not be directly on the page object)
    // Ideally we'd look up file metadata, but for now fallback to "File X"
    const fileName = `File ${routeFileId}`;
    if (pageInFile) {
      ClientLoggingService.log(
        'DEBUG',
        `[ChatContextSync] Found page in file ${routeFileId}: page ${pageInFile.getPageNumber?.() || 'unknown'}`
      );
    }

    return { fileId: routeFileId, fileName };
  }

  /**
   * Logs the context update for debugging.
   */
  private logContextUpdate(
    routeContext: RouteChatContext,
    resolvedFileId: string | undefined,
    projectName: string,
    fileName: string | undefined
  ): void {
    ClientLoggingService.log('INFO', '[ChatContextSync] Updated chat context:', {
      projectId: routeContext.projectId,
      projectName,
      fileId: resolvedFileId,
      fileName,
      pageNumber: routeContext.pageNumber,
      routeFileId: routeContext.fileId, // What's in URL (may differ from resolvedFileId)
      note:
        resolvedFileId === undefined && routeContext.fileId === '1'
          ? 'Legacy project - fileId omitted for backend'
          : 'Modern project or no fileId',
    });
  }
}
